/**
 * Rummy 500 — server-authoritative rules engine (4 players, individual scores, one 52-card deck).
 *
 * Rules implemented (toggles live in {@link Rummy500Config}):
 *  - Seven cards each; the top card of the stock starts the discard pile.
 *  - A turn is: draw (the top stock card, or take a card from the discard pile together with every card on top of it),
 *    then any number of `meld` / `layoff` actions, then a discard. Melding or laying off the last card goes out too.
 *  - Taking from the pile: the deepest card taken (the one you pointed at) must be used in a meld or laid off this turn
 *    — the engine only lets you take it when you can, and you cannot discard before you did (unless it has become
 *    unusable since). The other cards taken join your hand.
 *  - A meld is a set (three or four cards of one rank, all different suits) or a run (three or more consecutive cards
 *    of one suit; the ace counts low in A-2-3 and high in Q-K-A, never both: no wrap-around). `layoff` adds one card to
 *    any player's meld: a set takes the missing suits, a run grows at either end.
 *  - Scoring: cards melded or laid off count for the player who played them (ace 15 in sets and in Q-K-A runs, 1 in
 *    A-2-3 runs; 2–9 face value; 10, J, Q, K 10); cards left in hand count against their holder (ace 15). There is no
 *    going-out bonus. A hand ends when a player has no cards left or when the stock runs out at the start of a draw.
 *  - The match ends when a player reaches `targetScore` at the end of a hand and leads alone; `maxHands` is a hard cap.
 *    Standings are by score, equal scores share a place.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface Rummy500Config {
  /** Match ends when a player reaches this score at the end of a hand and leads alone. */
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxHands: number;
  handEndDelayMs: number;
}

export type Rummy500Phase = 'DRAW' | 'PLAY' | 'HAND_END' | 'MATCH_END';

/** A meld on the table. `seq` holds the run positions (ace 1 or 14) and is empty for sets. */
export interface Rummy500Meld {
  id: number;
  kind: 'set' | 'run';
  cards: CardId[];
  /** Seat that played each card (same order as `cards`). */
  by: number[];
  seq: number[];
}

/** One player's result for a finished hand. */
export interface Rummy500HandResult {
  melded: number;
  hand: number;
  total: number;
}

export interface Rummy500State {
  config: Rummy500Config;
  players: string[];
  hand: number;
  dealer: number;
  phase: Rummy500Phase;
  hands: CardId[][];
  stock: CardId[];
  /** Discard pile, top card last. */
  pile: CardId[];
  melds: Rummy500Meld[];
  nextMeldId: number;
  turn: number;
  /** The card taken from the pile that still has to be used this turn. */
  mustUse: CardId | null;
  /** Seat that went out this hand (null while playing or after a stock-out). */
  wentOut: number | null;
  scores: number[];
  handResults: Rummy500HandResult[][];
}

export type Rummy500Action =
  | { type: 'draw' }
  | { type: 'takePile'; card: CardId }
  | { type: 'meld'; cards: CardId[] }
  | { type: 'layoff'; meldId: number; card: CardId }
  | { type: 'discard'; card: CardId };

export type Rummy500Legal =
  | { kind: 'draw'; /** Pile cards that may be taken (each also takes the cards above it). */ takeable: CardId[] }
  | { kind: 'play'; mustUse: CardId | null; discard: CardId[] }
  | { kind: 'none' };

export interface Rummy500View {
  gameType: 'RUMMY_500';
  config: Rummy500Config;
  seat: number | null;
  phase: Rummy500Phase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  stockCount: number;
  pile: CardId[];
  melds: Rummy500Meld[];
  mustUse: CardId | null;
  wentOut: number | null;
  scores: number[];
  handResults: Rummy500HandResult[][];
  /** Same as `scores` so generic score UIs work. */
  totals: number[];
}

const DEFAULTS: Rummy500Config = {
  targetScore: 500,
  maxHands: 10,
  handEndDelayMs: 5000,
};

const SEATS = 4;
const HAND_SIZE = 7;

const rankOf = (id: CardId): number => parseCard(id).rank;
const suitOf = (id: CardId): string => parseCard(id).suit;

/** Card value for an ace played at `pos` (1 = low, 14 = high / in a set) or any other rank. */
export function rummy500CardValue(rank: number, pos: number = rank): number {
  if (rank === 14) return pos === 1 ? 1 : 15;
  if (rank >= 10) return 10;
  return rank;
}

/** Value of a card left in hand. */
export const rummy500HandValue = (id: CardId): number => rummy500CardValue(rankOf(id));

/**
 * Orders `cards` as a run (ace low or high) and returns the positions, or null when they are not a run of 3+.
 * The returned `cards` are sorted by position.
 */
export function rummy500RunOrder(cards: readonly CardId[]): { cards: CardId[]; seq: number[] } | null {
  if (cards.length < 3 || cards.length > 13) return null;
  const suit = suitOf(cards[0]!);
  if (cards.some((c) => suitOf(c) !== suit)) return null;
  for (const aceLow of [true, false]) {
    const items = cards.map((c) => ({ c, p: rankOf(c) === 14 && aceLow ? 1 : rankOf(c) })).sort((a, b) => a.p - b.p);
    if (items.every((it, i) => i === 0 || it.p === items[i - 1]!.p + 1)) return { cards: items.map((i) => i.c), seq: items.map((i) => i.p) };
  }
  return null;
}

/** Null when `cards` are a legal new meld (set or run); otherwise why not. */
export function rummy500MeldError(cards: readonly CardId[]): string | null {
  if (cards.length < 3) return 'A meld needs at least three cards';
  if (new Set(cards).size !== cards.length) return 'Duplicate card';
  const r = rankOf(cards[0]!);
  if (cards.every((c) => rankOf(c) === r)) {
    if (cards.length > 4) return 'A set has at most four cards';
    if (new Set(cards.map(suitOf)).size !== cards.length) return 'A set needs different suits';
    return null;
  }
  return rummy500RunOrder(cards) ? null : 'Not a set or a run';
}

/** Where `card` would go on `meld`: 'set', 'low' or 'high' — or null when it cannot be laid off. */
export function rummy500LayoffSpot(meld: Rummy500Meld, card: CardId): 'set' | 'low' | 'high' | null {
  if (meld.kind === 'set') {
    if (meld.cards.length >= 4 || rankOf(card) !== rankOf(meld.cards[0]!)) return null;
    return meld.cards.some((c) => suitOf(c) === suitOf(card)) ? null : 'set';
  }
  if (suitOf(card) !== suitOf(meld.cards[0]!)) return null;
  const r = rankOf(card);
  const lo = meld.seq[0]!;
  const hi = meld.seq[meld.seq.length - 1]!;
  if (lo > 1 && (r === lo - 1 || (r === 14 && lo === 2))) return 'low';
  if (hi < 14 && r === hi + 1 && !(meld.seq[0] === 1 && hi + 1 === 14)) return 'high';
  return null;
}

/** True when `card` can be used this turn: in a new meld with `hand` cards, or laid off on a table meld. */
export function rummy500CanUse(card: CardId, hand: readonly CardId[], melds: readonly Rummy500Meld[]): boolean {
  if (melds.some((m) => rummy500LayoffSpot(m, card) !== null)) return true;
  const others = hand.filter((c) => c !== card);
  const r = rankOf(card);
  if (new Set(others.filter((c) => rankOf(c) === r).map(suitOf)).size >= 2) return true;
  const same = [card, ...others.filter((c) => suitOf(c) === suitOf(card))];
  for (const aceLow of [true, false]) {
    const pos = (c: CardId) => (rankOf(c) === 14 && aceLow ? 1 : rankOf(c));
    const have = new Set(same.map(pos));
    const p = pos(card);
    let lo = p;
    while (have.has(lo - 1)) lo--;
    let hi = p;
    while (have.has(hi + 1)) hi++;
    if (hi - lo >= 2) return true;
  }
  return false;
}

/** The single action that uses `card` (a set/run from the hand, or a lay-off), or null. Used by bots and timeouts. */
export function rummy500FindUse(card: CardId, hand: readonly CardId[], melds: readonly Rummy500Meld[]): Rummy500Action | null {
  for (const m of melds) if (rummy500LayoffSpot(m, card) !== null) return { type: 'layoff', meldId: m.id, card };
  const others = hand.filter((c) => c !== card);
  const r = rankOf(card);
  const bySuit = new Map<string, CardId>();
  for (const c of others) if (rankOf(c) === r && !bySuit.has(suitOf(c))) bySuit.set(suitOf(c), c);
  if (bySuit.size >= 2) return { type: 'meld', cards: [card, ...bySuit.values()] };
  const same = [card, ...others.filter((c) => suitOf(c) === suitOf(card))];
  for (const aceLow of [true, false]) {
    const pos = (c: CardId) => (rankOf(c) === 14 && aceLow ? 1 : rankOf(c));
    const byPos = new Map<number, CardId>();
    for (const c of same) byPos.set(pos(c), c);
    const p = pos(card);
    let lo = p;
    while (byPos.has(lo - 1)) lo--;
    let hi = p;
    while (byPos.has(hi + 1)) hi++;
    if (hi - lo >= 2) {
      const out: CardId[] = [];
      for (let k = lo; k <= hi; k++) out.push(byPos.get(k)!);
      return { type: 'meld', cards: out };
    }
  }
  return null;
}

/** Pile cards the player may take: the card must be usable once it and everything above it are in hand. */
export function rummy500Takeable(pile: readonly CardId[], hand: readonly CardId[], melds: readonly Rummy500Meld[]): CardId[] {
  const out: CardId[] = [];
  for (let i = 0; i < pile.length; i++) {
    if (rummy500CanUse(pile[i]!, [...hand, ...pile.slice(i)], melds)) out.push(pile[i]!);
  }
  return out;
}

/** Points the cards of `m` played by `seat` are worth. */
function meldPoints(m: Rummy500Meld, seat: number): number {
  let n = 0;
  m.cards.forEach((c, i) => {
    if (m.by[i] !== seat) return;
    n += rummy500CardValue(rankOf(c), m.kind === 'run' ? m.seq[i]! : 14);
  });
  return n;
}

function standingsOf(scores: readonly number[]): Standing[] {
  const order = scores.map((score, seat) => ({ seat, score })).sort((a, b) => b.score - a.score || a.seat - b.seat);
  const out: Standing[] = [];
  order.forEach((s, i) => {
    const prev = out[i - 1];
    out.push({ seat: s.seat, score: s.score, place: prev && prev.score === s.score ? prev.place : i + 1 });
  });
  return out.sort((a, b) => a.seat - b.seat);
}

function deal(state: Rummy500State, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  state.hands = Array.from({ length: SEATS }, (_, i) => sortHand(deck.slice(i * HAND_SIZE, (i + 1) * HAND_SIZE)));
  state.stock = deck.slice(SEATS * HAND_SIZE);
  state.pile = [state.stock.pop()!];
  state.melds = [];
  state.nextMeldId = 1;
  state.mustUse = null;
  state.wentOut = null;
  state.turn = (state.dealer + 1) % SEATS;
  state.phase = 'DRAW';
  events.push({ type: 'dealt', dealer: state.dealer, counts: state.hands.map((h) => h.length), pileTop: state.pile[0] });
}

function finishHand(state: Rummy500State, wentOut: number | null, events: EngineEvent[]): void {
  state.wentOut = wentOut;
  state.mustUse = null;
  const results: Rummy500HandResult[] = state.hands.map((h, seat) => {
    const melded = state.melds.reduce((n, m) => n + meldPoints(m, seat), 0);
    const hand = h.reduce((n, c) => n + rummy500HandValue(c), 0);
    return { melded, hand, total: melded - hand };
  });
  results.forEach((r, seat) => {
    state.scores[seat]! += r.total;
  });
  state.handResults.push(results);
  events.push({ type: 'handEnd', hand: state.hand, wentOut, results, scores: [...state.scores] });
  const top = Math.max(...state.scores);
  const leaders = state.scores.filter((s) => s === top).length;
  const reached = top >= state.config.targetScore && leaders === 1;
  const capped = state.hand >= state.config.maxHands;
  if (reached || capped) {
    state.phase = 'MATCH_END';
    events.push({ type: 'matchEnd', standings: standingsOf(state.scores) });
  } else {
    state.phase = 'HAND_END';
  }
}

export const rummy500Engine: GameEngine<Rummy500Config, Rummy500State, Rummy500Action, Rummy500View, Rummy500Legal> = {
  gameType: 'RUMMY_500',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 50, 5000);
    checkInt('maxHands', c.maxHands, 1, 30);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Rummy 500 needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: Rummy500State = {
      config,
      players,
      hand: 1,
      dealer: rng.int(SEATS),
      phase: 'DRAW',
      hands: [],
      stock: [],
      pile: [],
      melds: [],
      nextMeldId: 1,
      turn: 0,
      mustUse: null,
      wentOut: null,
      scores: Array<number>(SEATS).fill(0),
      handResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    const card = (v: unknown): CardId => {
      if (typeof v !== 'string' || v.length > 6) throw new EngineError('INVALID_ACTION', 'Bad card');
      return v;
    };
    if (raw.type === 'draw') return { type: 'draw' };
    if (raw.type === 'takePile') return { type: 'takePile', card: card(raw.card) };
    if (raw.type === 'discard') return { type: 'discard', card: card(raw.card) };
    if (raw.type === 'layoff' && typeof raw.meldId === 'number' && Number.isInteger(raw.meldId)) return { type: 'layoff', meldId: raw.meldId, card: card(raw.card) };
    if (raw.type === 'meld' && Array.isArray(raw.cards) && raw.cards.length >= 3 && raw.cards.length <= 13) return { type: 'meld', cards: raw.cards.map(card) };
    throw new EngineError('INVALID_ACTION', 'Unknown Rummy 500 action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END' && state.phase !== 'HAND_END', 'GAME_OVER', 'Hand is over');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;

    if (action.type === 'draw') {
      assertEngine(state.phase === 'DRAW', 'ILLEGAL_MOVE', 'Already drew');
      assertEngine(state.stock.length > 0, 'ILLEGAL_MOVE', 'The stock is empty');
      hand.push(state.stock.pop()!);
      state.hands[seat] = sortHand(hand);
      state.phase = 'PLAY';
      events.push({ type: 'draw', seat });
      return events;
    }

    if (action.type === 'takePile') {
      assertEngine(state.phase === 'DRAW', 'ILLEGAL_MOVE', 'Already drew');
      const at = state.pile.indexOf(action.card);
      assertEngine(at >= 0, 'INVALID_CARD', 'That card is not in the pile');
      const taken = state.pile.slice(at);
      assertEngine(rummy500CanUse(action.card, [...hand, ...taken], state.melds), 'ILLEGAL_MOVE', 'You must be able to use that card this turn');
      state.pile = state.pile.slice(0, at);
      hand.push(...taken);
      state.hands[seat] = sortHand(hand);
      state.mustUse = action.card;
      state.phase = 'PLAY';
      events.push({ type: 'takePile', seat, card: action.card, cards: taken });
      return events;
    }

    assertEngine(state.phase === 'PLAY', 'ILLEGAL_MOVE', 'Draw first');

    if (action.type === 'meld') {
      assertEngine(action.cards.every((c) => hand.includes(c)), 'INVALID_CARD', 'Card not in hand');
      const err = rummy500MeldError(action.cards);
      assertEngine(!err, 'ILLEGAL_MOVE', err ?? '');
      const isSet = action.cards.every((c) => rankOf(c) === rankOf(action.cards[0]!));
      const run = isSet ? null : rummy500RunOrder(action.cards)!;
      removeCards(hand, action.cards);
      const cards = run ? run.cards : sortHand(action.cards);
      state.melds.push({ id: state.nextMeldId++, kind: isSet ? 'set' : 'run', cards, by: cards.map(() => seat), seq: run ? run.seq : [] });
      if (state.mustUse && action.cards.includes(state.mustUse)) state.mustUse = null;
      events.push({ type: 'meld', seat, cards });
      if (hand.length === 0) finishHand(state, seat, events);
      return events;
    }

    if (action.type === 'layoff') {
      assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
      const m = state.melds.find((x) => x.id === action.meldId);
      assertEngine(m !== undefined, 'ILLEGAL_MOVE', 'No such meld');
      const spot = rummy500LayoffSpot(m!, action.card);
      assertEngine(spot !== null, 'ILLEGAL_MOVE', 'That card does not fit the meld');
      removeCards(hand, [action.card]);
      if (spot === 'low') {
        m!.cards.unshift(action.card);
        m!.by.unshift(seat);
        m!.seq.unshift(rankOf(action.card) === 14 ? 1 : rankOf(action.card));
      } else if (spot === 'high') {
        m!.cards.push(action.card);
        m!.by.push(seat);
        m!.seq.push(rankOf(action.card) === 14 ? 14 : rankOf(action.card));
      } else {
        m!.cards.push(action.card);
        m!.by.push(seat);
      }
      if (state.mustUse === action.card) state.mustUse = null;
      events.push({ type: 'layoff', seat, meldId: m!.id, card: action.card });
      if (hand.length === 0) finishHand(state, seat, events);
      return events;
    }

    // discard
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    if (state.mustUse && hand.includes(state.mustUse) && rummy500CanUse(state.mustUse, hand, state.melds)) {
      throw new EngineError('ILLEGAL_MOVE', 'Use the card you took from the pile first');
    }
    state.mustUse = null;
    removeCards(hand, [action.card]);
    state.pile.push(action.card);
    events.push({ type: 'discard', seat, card: action.card });
    if (hand.length === 0) {
      finishHand(state, seat, events);
      return events;
    }
    state.turn = (seat + 1) % SEATS;
    state.phase = 'DRAW';
    return events;
  },

  getActors(state) {
    if (state.phase !== 'DRAW' && state.phase !== 'PLAY') return [];
    if (state.phase === 'DRAW' && state.stock.length === 0) return [];
    return [state.turn];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    const hand = state.hands[seat]!;
    if (state.phase === 'DRAW') {
      if (state.stock.length === 0) return { kind: 'none' };
      return { kind: 'draw', takeable: rummy500Takeable(state.pile, hand, state.melds) };
    }
    if (state.phase === 'PLAY') {
      const pending = state.mustUse !== null && hand.includes(state.mustUse) && rummy500CanUse(state.mustUse, hand, state.melds);
      return { kind: 'play', mustUse: pending ? state.mustUse : null, discard: pending ? [] : [...hand] };
    }
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'DRAW') return { type: 'draw' };
    const hand = state.hands[seat]!;
    if (state.mustUse && hand.includes(state.mustUse) && rummy500CanUse(state.mustUse, hand, state.melds)) {
      const use = rummy500FindUse(state.mustUse, hand, state.melds);
      if (use) return use;
    }
    const worst = [...hand].sort((a, b) => rummy500HandValue(b) - rummy500HandValue(a))[0]!;
    return { type: 'discard', card: worst };
  },

  getView(state, seat) {
    return {
      gameType: 'RUMMY_500',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.hand,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      stockCount: state.stock.length,
      pile: [...state.pile],
      melds: state.melds.map((m) => ({ id: m.id, kind: m.kind, cards: [...m.cards], by: [...m.by], seq: [...m.seq] })),
      mustUse: seat === state.turn ? state.mustUse : null,
      wentOut: state.wentOut,
      scores: [...state.scores],
      handResults: state.handResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [...state.scores],
    };
  },

  getSystemStep(state) {
    if (state.phase === 'DRAW' && state.stock.length === 0) return { kind: 'endHand', delayMs: 1200 };
    if (state.phase === 'HAND_END') return { kind: 'nextHand', delayMs: state.config.handEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'DRAW' && state.stock.length === 0) {
      finishHand(state, null, events);
      return events;
    }
    if (state.phase === 'HAND_END') {
      state.hand++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standingsOf(state.scores);
  },

  getRoundsCompleted: (state) => state.handResults.length,
};
